/**
 * MockSetupPage — 模擬試験設定 (FR-09 / EF-01 / EF-03)
 * exam_spec マスタを取得して構成表示。中断中セッションがあれば再開バナー。
 * 経験則 #15: 非同期中はボタン disabled
 * 経験則 #68: 合否目安の断定語を使わない
 */

import { useEffect, useState, useContext } from 'react'
import { useNavigate } from 'react-router-dom'
import { getExamSpec, getActiveMockExam, startMockExam, discardMockExam } from '../api'
import { ApiClientError } from '../api/client'
import type { ExamSpec, MockExamSession, CertificationType } from '../api/types'
import { AuthContext } from '../contexts/AuthContext'
import Icon from '../components/Icon'
import Spinner, { PageLoader } from '../components/Spinner'

export default function MockSetupPage() {
  const { certType } = useContext(AuthContext)
  const navigate = useNavigate()

  const [spec, setSpec] = useState<ExamSpec | null>(null)
  const [activeSession, setActiveSession] = useState<MockExamSession | null>(null)
  const [loading, setLoading] = useState(true)
  const [starting, setStarting] = useState(false)
  const [discarding, setDiscarding] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!certType) return
    setLoading(true)
    setError(null)
    Promise.all([getExamSpec(certType as CertificationType), getActiveMockExam()])
      .then(([s, active]) => {
        setSpec(s)
        setActiveSession(active)
      })
      .catch((err) => {
        if (err instanceof ApiClientError) setError(err.message)
        else setError('データの取得に失敗しました')
      })
      .finally(() => setLoading(false))
  }, [certType])

  const handleStart = async () => {
    if (!certType) return
    setStarting(true)
    setError(null)
    try {
      const session = await startMockExam({ certification_type: certType as CertificationType })
      if (session?.id) {
        if (session.total_questions) {
          sessionStorage.setItem(`mock_total_${session.id}`, String(session.total_questions))
        }
        if (session.remaining_seconds) {
          sessionStorage.setItem(`mock_remaining_${session.id}`, String(session.remaining_seconds))
        }
      }
      navigate(`/mock-exam/session/${session?.id}/1`)
    } catch (err) {
      if (err instanceof ApiClientError) setError(err.message)
      else setError('模擬試験の開始に失敗しました')
    } finally {
      setStarting(false)
    }
  }

  const handleResume = () => {
    if (!activeSession?.id) return
    if (activeSession.remaining_seconds != null) {
      sessionStorage.setItem(`mock_remaining_${activeSession.id}`, String(activeSession.remaining_seconds))
    }
    if (activeSession.total_questions != null) {
      sessionStorage.setItem(`mock_total_${activeSession.id}`, String(activeSession.total_questions))
    }
    navigate(`/mock-exam/session/${activeSession.id}/1`)
  }

  const handleDiscard = async () => {
    if (!activeSession?.id) return
    if (!window.confirm('中断中の模擬試験を破棄しますか？この操作は元に戻せません。')) return
    setDiscarding(true)
    try {
      await discardMockExam(activeSession.id)
      setActiveSession(null)
    } catch (err) {
      if (err instanceof ApiClientError) setError(err.message)
      else setError('破棄に失敗しました')
    } finally {
      setDiscarding(false)
    }
  }

  if (!certType) return null

  return (
    <div className="mx-auto max-w-content p-screen">
      <h1 className="mb-5 text-[22px] font-bold tracking-tight text-text-primary">模擬試験</h1>

      {error && (
        <div role="alert" className="alert-error mb-4">
          {error}
        </div>
      )}

      {/* 中断中バナー (EF-01) */}
      {activeSession && (
        <div data-testid="mock-resume" className="mb-5 rounded-lg border border-review bg-review-bg px-4 py-3.5">
          <div className="mb-2.5 flex items-center gap-2 text-review">
            <Icon name="clock" size={16} />
            <span className="text-[15px] font-bold">中断中の模擬試験があります</span>
          </div>
          <div className="mb-3 text-[13px] text-text-secondary">
            残り時間: {formatSeconds(activeSession.remaining_seconds ?? 0)}
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleResume}
              className="inline-flex h-10 flex-1 items-center justify-center rounded-md bg-review text-sm font-semibold text-white transition-opacity hover:opacity-90"
            >
              再開する
            </button>
            <button
              onClick={handleDiscard}
              disabled={discarding}
              className="inline-flex h-10 items-center justify-center rounded-md border border-border-strong bg-surface px-3.5 text-sm text-text-secondary transition-colors hover:bg-surface-alt disabled:cursor-not-allowed disabled:opacity-50"
            >
              {discarding ? '破棄中...' : '破棄'}
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <PageLoader />
      ) : spec ? (
        <>
          {/* 試験仕様 */}
          <div data-testid="mock-spec" className="card mb-5 p-4">
            <div className="mb-3 text-[13px] font-bold text-text-secondary">本番準拠の構成（exam_spec より）</div>
            <div className="mb-3.5 grid grid-cols-2 gap-3">
              <div className="rounded-md bg-surface-alt px-3 py-2.5 text-center">
                <div className="text-[11px] text-text-muted">出題数</div>
                <div className="mt-0.5 font-numeric text-xl font-bold">{spec.total_questions}問</div>
              </div>
              <div className="rounded-md bg-surface-alt px-3 py-2.5 text-center">
                <div className="text-[11px] text-text-muted">制限時間</div>
                <div className="mt-0.5 font-numeric text-xl font-bold">{spec.duration_minutes}分</div>
              </div>
            </div>

            {/* 科目構成 */}
            <div className="mb-2 text-[13px] text-text-secondary">科目構成</div>
            <div className="flex flex-col">
              {spec.subjects?.map((sub) => (
                <div key={sub.subject_key} className="flex justify-between border-b border-border py-1 text-[13px]">
                  <span className="text-text-primary">{sub.name_ja}</span>
                  <span className="font-semibold text-text-secondary">
                    {sub.questions}問 / {sub.points}点
                  </span>
                </div>
              ))}
            </div>

            <div className="mt-3 text-xs text-text-muted">
              合格基準: 全体{Math.round((spec.pass_total_ratio ?? 0.6) * 100)}%以上、 各科目
              {Math.round((spec.pass_subject_ratio ?? 0.4) * 100)}%以上（目安）
            </div>
          </div>

          {/* 開始ボタン */}
          <button
            data-testid="mock-start"
            onClick={handleStart}
            disabled={starting || !!activeSession}
            className="inline-flex h-[52px] w-full items-center justify-center gap-2 rounded-md bg-accent text-base font-bold text-text-onaccent transition-colors hover:bg-accent-hover disabled:cursor-not-allowed disabled:bg-text-muted disabled:opacity-50"
          >
            {starting ? <Spinner label="開始中..." /> : '模擬試験を始める'}
          </button>
          {activeSession && (
            <p className="mt-2 text-center text-xs text-text-muted">
              中断中の模試を先に再開または破棄してください
            </p>
          )}
        </>
      ) : (
        <div data-testid="mock-start-blocked" className="rounded-lg border border-border bg-surface px-6 py-10 text-center">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-surface-alt text-text-muted">
            <Icon name="clipboard" size={24} />
          </div>
          <div className="font-semibold text-text-primary">試験仕様が見つかりません</div>
          <div className="mt-1 text-sm text-text-muted">しばらくお待ちください</div>
        </div>
      )}
    </div>
  )
}

function formatSeconds(seconds: number): string {
  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  const s = seconds % 60
  return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
}
